import { create } from 'zustand'
import type { TableSchema } from '@features/tables/models'
import { reconcileColumnStateWithSchema } from '../../columns/logic/columnState'

interface TableState {
  selectedTableId: string | null
  schema: TableSchema | null
  columnVisibility: Record<string, boolean>
  columnOrder: string[]
  columnWidths: Record<string, number>

  selectTable: (tableId: string) => void
  setSchema: (schema: TableSchema) => void
  toggleColumn: (columnId: string) => void
  setColumnVisibility: (columnId: string, visible: boolean) => void
  setColumnVisibilityMap: (visibility: Record<string, boolean>) => void
  setColumnState: (columnOrder: string[], columnVisibility: Record<string, boolean>) => void
  showAllColumns: () => void
  hideAllColumns: () => void
  setColumnOrder: (columnOrder: string[]) => void
  /** Remove a deleted column from schema and local presentation metadata atomically. */
  removeColumn: (columnId: string) => void
  reorderColumns: (fromIndex: number, toIndex: number) => void
  previewResizeColumn: (columnId: string, width: number) => void
  resizeColumn: (columnId: string, width: number) => void
  setColumnWidths: (widths: Record<string, number>) => void
  updateColumnMetadata: (
    columnId: string,
    metadata: {
      displayName: string
      descriptionUser: string
      descriptionAgent: string
      displayFormatId?: string | null
      displayFormatName?: string | null
      displayDecimalPlaces?: number | null
    }
  ) => void
  updateColumnFormula: (columnId: string, formula: string) => void
}

export function loadColumnWidths(tableId: string): Record<string, number> {
  try {
    const stored = localStorage.getItem(`erp-col-widths-${tableId}`)
    return stored ? (JSON.parse(stored) as Record<string, number>) : {}
  } catch {
    return {}
  }
}

function saveColumnWidths(tableId: string, widths: Record<string, number>): void {
  localStorage.setItem(`erp-col-widths-${tableId}`, JSON.stringify(widths))
}

export const useTableStore = create<TableState>((set, _get) => ({
  selectedTableId: null,
  schema: null,
  columnVisibility: {},
  columnOrder: [],
  columnWidths: {},

  selectTable: (tableId) => {
    const widths = loadColumnWidths(tableId)
    set({
      selectedTableId: tableId,
      schema: null,
      columnVisibility: {},
      columnOrder: [],
      columnWidths: widths,
    })
  },

  setSchema: (schema) =>
    set((state) => {
      // A refresh of the loaded table (column rename, formula edit, new field)
      // keeps the user's column arrangement; only a different table starts over
      // with every column visible in schema order.
      if (state.schema?.table_id === schema.table_id) {
        return {
          schema,
          ...reconcileColumnStateWithSchema(
            schema.columns,
            state.columnOrder,
            state.columnVisibility,
            null
          ),
        }
      }

      const columnOrder = schema.columns.map((c) => c.id)
      const columnVisibility: Record<string, boolean> = {}
      for (const col of schema.columns) {
        columnVisibility[col.id] = true
      }
      return { schema, columnOrder, columnVisibility }
    }),

  toggleColumn: (columnId) =>
    set((state) => ({
      columnVisibility: {
        ...state.columnVisibility,
        [columnId]: !state.columnVisibility[columnId],
      },
    })),

  setColumnVisibility: (columnId, visible) =>
    set((state) => ({
      columnVisibility: {
        ...state.columnVisibility,
        [columnId]: visible,
      },
    })),

  setColumnVisibilityMap: (visibility) =>
    set(() => ({
      columnVisibility: { ...visibility },
    })),

  setColumnState: (columnOrder, columnVisibility) =>
    set(() => ({
      columnOrder: [...columnOrder],
      columnVisibility: { ...columnVisibility },
    })),

  showAllColumns: () =>
    set((state) => {
      const vis: Record<string, boolean> = {}
      for (const id of state.columnOrder) {
        vis[id] = true
      }
      return { columnVisibility: vis }
    }),

  hideAllColumns: () =>
    set((state) => {
      const vis: Record<string, boolean> = {}
      for (const id of state.columnOrder) {
        vis[id] = false
      }
      return { columnVisibility: vis }
    }),

  setColumnOrder: (columnOrder) =>
    set(() => ({
      columnOrder: [...columnOrder],
    })),

  removeColumn: (columnId) =>
    set((state) => {
      const columnVisibility = { ...state.columnVisibility }
      const columnWidths = { ...state.columnWidths }
      delete columnVisibility[columnId]
      delete columnWidths[columnId]

      if (state.selectedTableId) {
        saveColumnWidths(state.selectedTableId, columnWidths)
      }

      return {
        schema: state.schema
          ? {
              ...state.schema,
              columns: state.schema.columns.filter((column) => column.id !== columnId),
            }
          : null,
        columnOrder: state.columnOrder.filter((id) => id !== columnId),
        columnVisibility,
        columnWidths,
      }
    }),

  reorderColumns: (fromIndex, toIndex) =>
    set((state) => {
      const order = [...state.columnOrder]
      const [moved] = order.splice(fromIndex, 1)
      order.splice(toIndex, 0, moved)
      return { columnOrder: order }
    }),

  previewResizeColumn: (columnId, width) =>
    set((state) => {
      if (state.columnWidths[columnId] === width) return state

      return {
        columnWidths: { ...state.columnWidths, [columnId]: width },
      }
    }),

  resizeColumn: (columnId, width) =>
    set((state) => {
      const widths =
        state.columnWidths[columnId] === width
          ? state.columnWidths
          : { ...state.columnWidths, [columnId]: width }
      if (state.selectedTableId) {
        saveColumnWidths(state.selectedTableId, widths)
      }
      if (widths === state.columnWidths) return state
      return { columnWidths: widths }
    }),

  setColumnWidths: (nextWidths) =>
    set((state) => {
      const widths = { ...state.columnWidths, ...nextWidths }
      if (state.selectedTableId) {
        saveColumnWidths(state.selectedTableId, widths)
      }
      return { columnWidths: widths }
    }),

  updateColumnMetadata: (columnId, metadata) =>
    set((state) => {
      if (!state.schema) return {}

      return {
        schema: {
          ...state.schema,
          columns: state.schema.columns.map((column) =>
            column.id === columnId
              ? {
                  ...column,
                  display_name: metadata.displayName,
                  description_user: metadata.descriptionUser || null,
                  description_agent: metadata.descriptionAgent || null,
                  display_format_id: metadata.displayFormatId ?? column.display_format_id,
                  display_format_name: metadata.displayFormatName ?? column.display_format_name,
                  display_decimal_places:
                    metadata.displayDecimalPlaces ?? column.display_decimal_places,
                }
              : column
          ),
        },
      }
    }),

  updateColumnFormula: (columnId, formula) =>
    set((state) => {
      if (!state.schema) return {}

      return {
        schema: {
          ...state.schema,
          columns: state.schema.columns.map((column) =>
            column.id === columnId ? { ...column, formula } : column
          ),
        },
      }
    }),
}))
